import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { HiFingerPrint, HiX } from "react-icons/hi";
import useAuth from "../../../../hooks/useAuth";
import SecureStorage from "../../../../utils/secureStorage";
import { CustomButton } from "../../../atoms/CustomButton";
import {
  isPasskeySupported,
  notifyPasskeyError,
  useListPasskeys,
  useRegisterPasskey,
} from "../../../../hooks/auth/passwordless";

/**
 * Offers a passkey right after a successful sign-in — the one moment the user has just proved who
 * they are and adding one costs a click. Settings alone only reaches people who go looking, and an
 * empty passkey estate is what makes the login screen's passkey option useless.
 *
 * Shows nothing when the browser cannot do WebAuthn, when the user already has a key, or once
 * they have declined. The refusal is per browser and permanent.
 *
 * Nor in an impersonation tab. The prompt reads "you have no passkey yet", which is true of the
 * owner and beside the point: the fingerprint it would ask for belongs to the support operator,
 * and the credential would outlive the impersonation session by years. UMD refuses the enrolment
 * anyway, so this only spares the operator a button that answers 403; the guard that matters is
 * the server one.
 */

const DISMISSED_KEY = "passkey_prompt_dismissed";

const wasDismissed = () => {
  try {
    return localStorage.getItem(DISMISSED_KEY) === "true";
  } catch {
    return false;
  }
};

const PasskeyEnrolmentPrompt = () => {
  const [t] = useTranslation();
  const { user } = useAuth();

  const [visible, setVisible] = useState(false);
  const { mutateAsync: fetchPasskeys } = useListPasskeys();
  const { mutate: registerPasskey, isLoading: isRegistering } =
    useRegisterPasskey();

  const entityIdentification = user?.entity_identification;
  const userIdentification = user?.user_identification;

  const decide = useCallback(async () => {
    if (
      !isPasskeySupported() ||
      wasDismissed() ||
      SecureStorage.isImpersonationTab() ||
      !entityIdentification ||
      !userIdentification
    ) {
      return;
    }
    try {
      const response = await fetchPasskeys({
        entity_identification: entityIdentification,
        user_identification: userIdentification,
      });
      setVisible((response?.data?.element || []).length === 0);
    } catch {
      setVisible(false);
    }
  }, [entityIdentification, userIdentification, fetchPasskeys]);

  useEffect(() => {
    decide();
  }, [decide]);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, "true");
    } catch {}
    setVisible(false);
  };

  const onAdd = () => {
    registerPasskey(
      {
        entity_identification: entityIdentification as string,
        user_identification: userIdentification as string,
      },
      {
        onSuccess: () => {
          toast.success(t("passkey.added"));
          setVisible(false);
        },
        onError: (error: any) => {
          notifyPasskeyError(error, t, toast);
        },
      }
    );
  };

  if (!visible) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-4 sm:flex-row sm:flex-wrap sm:items-start">
      <HiFingerPrint
        size={20}
        className="mt-0.5 shrink-0 text-blue-600"
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1 sm:basis-64">
        <p className="text-sm font-semibold text-gray-900">
          {t("passkey.promptTitle")}
        </p>
        <p className="mt-0.5 text-xs text-gray-500">
          {t("passkey.promptBody")}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <CustomButton
          variant="primary"
          size="sm"
          title={t("passkey.addBtn")}
          isLoading={isRegistering}
          disabled={isRegistering}
          onClick={onAdd}
        />
        <CustomButton
          variant="none"
          size="icon"
          tooltip={t("passkey.promptDismiss") as string}
          icon={<HiX size={18} className="text-gray-400 hover:text-gray-600" />}
          onClick={dismiss}
        />
      </div>
    </div>
  );
};

export default PasskeyEnrolmentPrompt;
